import styles from './ProfileInsurance.module.scss';
import { Button, Container, GridCell, Heading, Icon, Link, LinkButton, Spinner } from 'ui/components';
import IconPlugCircle from 'ui/icons/icon-plug-circle.svg';
import Minus from 'ui/icons/icon-minus.svg';
import Plus from 'ui/icons/icon-plus.svg';
import { useState } from 'react';
import { Profiles } from 'application/repositories/profilesRepository';
import classNames from 'classnames';

export interface ProfileInsuranceProps {
	profile: Profiles;
	title?: string;
	subscribedText?: string;
	unsubscribedText?: string;
	groupLifeInsuranceIsSubscribed: boolean;
	groupLifeInsuranceManchet?: string;
	groupLifeInsuranceReadMoreBtn?: Umbraco.Link;
	fullTimeAccidentInsuranceIsSubscribed: boolean;
	fullTimeAccidentInsuranceManchet?: string;
	fullTimeAccidentInsuranceReadMoreBtn?: Umbraco.Link;
	otherInsurancesIsSubscribed: boolean;
	otherInsurancesIsLoading: boolean;
	otherInsurancesIsSuccess: boolean;
	otherInsurancesManchet?: string;
	otherInsurancesReadMoreBtn?: Umbraco.Link;
	otherInsurancesTermsOfConsentLink?: Umbraco.Link;
	otherInsurancesNoInsurancesInfoText?: string;
	otherInsurancesUnsubscribedInfoText?: string;
	otherInsurancesFailureInfoText?: string;
	otherInsurancesConsentBtnClick: () => void;
	otherInsurancesGoToMyAlkaBtn?: Umbraco.Link;
	otherInsurancesGiveConsentBtnText?: string;
	otherInsurancesWithdrawConsentBtnText?: string;
	otherInsurancesDetails: Array<{
		insurance: string;
	}>;
}

export const ProfileInsurance: React.FC<ProfileInsuranceProps> = ({
	profile,
	title = '',
	subscribedText = '',
	unsubscribedText = '',
	groupLifeInsuranceIsSubscribed,
	groupLifeInsuranceManchet = '',
	groupLifeInsuranceReadMoreBtn,
	fullTimeAccidentInsuranceIsSubscribed,
	fullTimeAccidentInsuranceManchet = '',
	fullTimeAccidentInsuranceReadMoreBtn,
	otherInsurancesIsSubscribed,
	otherInsurancesIsLoading,
	otherInsurancesIsSuccess,
	otherInsurancesManchet = '',
	otherInsurancesReadMoreBtn,
	otherInsurancesTermsOfConsentLink,
	otherInsurancesNoInsurancesInfoText = '',
	otherInsurancesUnsubscribedInfoText = '',
	otherInsurancesFailureInfoText = '',
	otherInsurancesConsentBtnClick,
	otherInsurancesGoToMyAlkaBtn,
	otherInsurancesGiveConsentBtnText = '',
	otherInsurancesWithdrawConsentBtnText = '',
	otherInsurancesDetails,
}) => {
	const [showMore, setShowMore] = useState(false);
	const [insuranceListOpen, setInsuranceListOpen] = useState(false);

	const toggleShowMore = () => {
		setShowMore(!showMore);
		setInsuranceListOpen(!insuranceListOpen);
	};

	if (!profile) {
		return <Spinner />;
	}

	return (
		<Container width="Full width">
			<GridCell>
				<article className={styles.ProfileInsurance}>
					<Heading headingLevel="h2" className={styles.ProfileInsurance_title}>
						<Icon className={styles.ProfileInsurance_titleIcon}>
							<IconPlugCircle />
						</Icon>
						<span>{title}</span>
					</Heading>
					<section className={styles.ProfileInsurance_columnsWrapper}>
						<div className={styles.ProfileInsurance_columns}>
							<article className={styles.ProfileInsurance_article}>
								<div>
									<span className={styles.ProfileInsurance_dataTitle}>{groupLifeInsuranceManchet}</span>
									<span className={styles.ProfileInsurance_dataValue}>
										{groupLifeInsuranceIsSubscribed ? subscribedText : unsubscribedText}
									</span>
								</div>
								{groupLifeInsuranceReadMoreBtn && (
									<div className={classNames(styles.ProfileInsurance_btnWrapper, styles.ProfileInsurance_btnWrapper___readMoreBtn)}>
										<LinkButton url={groupLifeInsuranceReadMoreBtn.url} style="negative">
											{groupLifeInsuranceReadMoreBtn.name}
										</LinkButton>
									</div>
								)}
							</article>
						</div>

						<div className={styles.ProfileInsurance_columns}>
							<article className={styles.ProfileInsurance_article}>
								<div>
									<span className={styles.ProfileInsurance_dataTitle}>{fullTimeAccidentInsuranceManchet}</span>
									<span className={styles.ProfileInsurance_dataValue}>
										{fullTimeAccidentInsuranceIsSubscribed ? subscribedText : unsubscribedText}
									</span>
								</div>
								{fullTimeAccidentInsuranceReadMoreBtn && (
									<div className={classNames(styles.ProfileInsurance_btnWrapper, styles.ProfileInsurance_btnWrapper___readMoreBtn)}>
										<LinkButton url={fullTimeAccidentInsuranceReadMoreBtn.url} style="negative">
											{fullTimeAccidentInsuranceReadMoreBtn.name}
										</LinkButton>
									</div>
								)}
							</article>
						</div>

						<div className={styles.ProfileInsurance_columns} aria-expanded={insuranceListOpen}>
							<article className={styles.ProfileInsurance_article}>
								<div>
									<span className={styles.ProfileInsurance_dataTitle}>{otherInsurancesManchet}</span>

									{otherInsurancesIsLoading && <Spinner />}

									{!otherInsurancesIsSuccess && !otherInsurancesIsLoading && otherInsurancesIsSubscribed && (
										<span className={styles.ProfileInsurance_dataValue}>
											{otherInsurancesFailureInfoText && otherInsurancesFailureInfoText}
										</span>
									)}

									{!otherInsurancesIsSuccess &&
										!otherInsurancesIsSubscribed &&
										!otherInsurancesIsLoading &&
										otherInsurancesUnsubscribedInfoText && (
											<span className={styles.ProfileInsurance_dataValue}>{otherInsurancesUnsubscribedInfoText}</span>
										)}

									{otherInsurancesIsSuccess && otherInsurancesIsSubscribed && !otherInsurancesIsLoading && (
										<>
											{otherInsurancesDetails && otherInsurancesDetails.length > 0 ? (
												<>
													{otherInsurancesDetails
														.slice(0, showMore ? otherInsurancesDetails.length : 2)
														.map((detail, idx) => (
															<p key={idx} className={styles.ProfileInsurance_list}>
																{detail.insurance}
															</p>
														))}
													{otherInsurancesDetails.length > 2 && (
														<aside onClick={toggleShowMore}>
															<button className={styles.ProfileInsurance_showMoreBtn}>
																{!showMore ? (
																	<>
																		<span>Vis mere</span>
																		<Icon className={styles.ProfileInsurance_IconPlus}>
																			<Plus />
																		</Icon>
																	</>
																) : (
																	<>
																		<span>Vis mindre</span>
																		<Icon className={styles.ProfileInsurance_IconMinus}>
																			<Minus />
																		</Icon>
																	</>
																)}
															</button>
														</aside>
													)}
												</>
											) : (
												otherInsurancesNoInsurancesInfoText && (
													<span className={styles.ProfileInsurance_dataValue}>{otherInsurancesNoInsurancesInfoText}</span>
												)
											)}
										</>
									)}
								</div>
								<div className={styles.ProfileInsurance_btnWrapper__otherInsurance}>
									{(!otherInsurancesIsSubscribed || otherInsurancesDetails.length === 0) && otherInsurancesReadMoreBtn && (
										<div>
											<LinkButton url={otherInsurancesReadMoreBtn.url} style="negative">
												{otherInsurancesReadMoreBtn.name}
											</LinkButton>
										</div>
									)}

									{otherInsurancesIsSubscribed && otherInsurancesDetails.length !== 0 && otherInsurancesGoToMyAlkaBtn && (
										<div>
											<LinkButton url={otherInsurancesGoToMyAlkaBtn.url} style="primary">
												{otherInsurancesGoToMyAlkaBtn.name}
											</LinkButton>
										</div>
									)}

									<div className={styles.ProfileInsurance_linkWrapper}>
										<div>
											{otherInsurancesConsentBtnClick && (
												<Button
													onClick={() => otherInsurancesConsentBtnClick()}
													style={otherInsurancesIsSubscribed ? 'negative' : 'primary'}
												>
													{otherInsurancesIsSubscribed
														? otherInsurancesWithdrawConsentBtnText
														: otherInsurancesGiveConsentBtnText}
												</Button>
											)}
										</div>
										<Link url={otherInsurancesTermsOfConsentLink?.url}>{otherInsurancesTermsOfConsentLink?.name}</Link>
									</div>
								</div>
							</article>
						</div>
					</section>
				</article>
			</GridCell>
		</Container>
	);
};
